// @ts-nocheck

import {
  Component,
  Host,
  h,
  Element,
  Event,
  EventEmitter,
  Prop,
  Watch,
  Listen,
} from '@stencil/core';
import { toggleDisableChildNodes } from '../../utils/toggleDisableChildNodes';
import './aha-menu-content-window-events';
// @ts-expect-error missing type definitions
import restrictScroll from 'restrict-scroll';

export interface AhaMenuContent extends HTMLAhaMenuContentElement {}

@Component({
  tag: 'aha-menu-content',
  styleUrl: 'aha-menu-content.css',
  shadow: true,
})
export class AhaMenuContent {
  @Element() host!: HTMLAhaMenuContentElement;

  /** Whether or not the element is open. Typically reflects the state of the parent <aha-menu> component. */
  @Prop({ reflect: true }) open: boolean = false;
  /**
   * If the `open` attribute is not set on the element, this applies display: none to the children of the element.
   * If the `open` attribute is set on the element, this applies display: block to the children of the element.
   * This is useful for hiding all the children of the element when the element is closed.
   */
  @Prop({ reflect: true, attribute: 'hide-children-when-closed' }) hideChildrenWhenClosed: boolean =
    false;
  @Watch('open')
  private handleOpenChange() {
    if (this.open) {
      this.openEvent.emit(this.host);
      restrictScroll.add(this.host);
      if (this.host.hasAttribute('popover')) this.host.showPopover?.();
    } else {
      if (this.host.hasAttribute('popover')) this.host.hidePopover?.();
      this.closeEvent.emit(this.host);
      restrictScroll.delete(this.host);
      this.closedEvent.emit(this.host);
    }
  }

  /** Whether or not the menu is currently disabled. */
  @Prop({ reflect: true }) disabled: boolean = false;
  @Watch('disabled')
  private disableChildren() {
    toggleDisableChildNodes(this.host);
  }

  /** Whether or not the menu restricts scroll to the open aha-menu-content element. By default, scrolling is restricted to within the open aha-menu-content element. */
  @Prop({ reflect: true, attribute: 'unrestricted-scroll' }) unrestrictedScroll: boolean = false;

  toggle() {
    if (this.disabled) return;
    this.open = !this.open;
  }

  // Begin the process of showing the <aha-menu-content> element.
  show() {
    if (this.disabled) return;
    this.open = true;
  }

  // Begin the process of fading out the <aha-menu-content> element.
  hide() {
    if (this.disabled) return;
    this.open = false;
  }

  private handleAnimationEnd(event: AnimationEvent) {
    switch (event.animationName) {
      case 'fadeIn':
        this.openedEvent.emit(this.host);
        break;
    }
  }

  // If the user presses the "Escape" key, then close the menu.
  private handleKeyup(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.hide();
      this.cancelEvent.emit(this.host);
    }
  }

  /**
   * Triggers when the menu is closed using the escape key.
   */
  @Event({
    eventName: 'aha-menu-content:cancel',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  /**
   * Triggers when the menu is initially opened but before it is visible.
   */
  @Event({
    eventName: 'aha-menu-content:open',
    // composed: true,
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  // If the <aha-menu-content> is directly opened, then if that <aha-menu-content> has a parent <aha-menu>
  // ensure the <aha-menu> is also opened.
  @Listen('aha-menu-content:open')
  private openAhaMenu() {
    const menu = this.host.closest('aha-menu');
    if (menu && !menu.open) {
      menu.open = true;
      menu.popperInstance = this.host;
      menu.positionPopper();
    }
  }

  /**
   * Triggers when the menu is visible on the page.
   */
  @Event({
    eventName: 'aha-menu-content:opened',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  // If the <aha-menu-content> is directly opened, then if that <aha-menu-content> has a parent <aha-menu>
  // ensure the <aha-menu> also fires its opened event.
  @Listen('aha-menu-content:opened')
  private openedAhaMenu() {
    const menu = this.host.closest('aha-menu');
    if (menu) menu.fireOpenedEvent();
  }

  /**
   * Triggers when the menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-menu-content:close',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers when the menu is no longer visible on the page.
   */
  @Event({
    eventName: 'aha-menu-content:closed',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  // If the <aha-menu-content> is directly closed, then if that <aha-menu-content> has a parent <aha-menu>
  // ensure the <aha-menu> is also closed and that the <aha-menu> also fires its closed event..
  @Listen('aha-menu-content:closed')
  private closedAhaMenu(event: CustomEvent) {
    if (event.detail !== this.host) {
      // this event propagates up the DOM tree, and we don't want to close the parent menu when its submenu closes
      return;
    }

    const menu = this.host.closest('aha-menu');
    if (menu) menu.fireClosedEvent();
  }

  @Listen('aha-menu-content:cancel')
  private cancelAhaMenu() {
    const menu = this.host.closest('aha-menu');
    if (menu) menu.fireCancelEvent();
  }

  private handleMouseLeave(event: MouseEvent) {
    const host = event.target;
    const menu = host.closest('aha-menu');

    // We only potentially take action if there is an assoctiated aha-menu element and that aha-menu element has a "submenu" attribute.
    if (!menu || (!menu.hasAttribute('submenu') && menu.getAttribute('trigger') !== 'hover')) {
      return;
    }

    menu.submenuHideFn();
  }

  private handleMouseEnter(event: MouseEvent) {
    const host = event.target;
    const menu = host.closest('aha-menu');
    if (menu && menu.submenu) {
      clearTimeout(menu.submenuHideTimeout);
    }
  }

  private handleSlotChange(event: any) {
    // Occasionally there is an <aha-menu-group> which _appears_ to be the last child of an <aha-menu-content>, but is in fact the
    // second-to-last child because the actual _last_ child is a <script> element. This can cause unwanted styling issues because
    // we will end up with too much space after the <aha-menu-group>. Code here just fixes that issue.
    const [secondToLastElem, lastElem] = event.target.assignedElements().slice(-2);
    const lastGroupClassName = 'last-child';
    event.target.assignedElements().forEach(elem => elem.classList.remove(lastGroupClassName));
    if (
      secondToLastElem &&
      lastElem &&
      secondToLastElem.tagName === 'AHA-MENU-GROUP' &&
      lastElem.tagName === 'SCRIPT'
    ) {
      secondToLastElem.classList.add(lastGroupClassName);
    }
  }

  // When the aha-menu-content component is first loaded, if the open attribute
  // is set to true, then we need to ensure that the aha-menu-content is open
  // and that the parent aha-menu (if it exists) is also open.
  componentDidLoad() {
    if (
      this.open &&
      !this.host.matches(':popover-open') &&
      window.featureFlags?.A_18022_add_popover_to_legacy_elements
    ) {
      this.handleOpenChange();
    }
  }

  render() {
    return (
      <Host
        popover="manual"
        onAnimationEnd={this.handleAnimationEnd}
        onKeyup={this.handleKeyup}
        onMouseLeave={this.handleMouseLeave}
        onMouseEnter={this.handleMouseEnter}
      >
        <div id="inner">
          <slot onSlotchange={this.handleSlotChange}></slot>
        </div>
      </Host>
    );
  }
}
